import { supabase } from "../lib/supabase";
import type { Database } from "../types/database";

export const inventoryService = {
  // Inventory Items
  async getInventoryItems(organizationId?: string) {
    // If no organizationId is provided, don't filter by it
    let query = supabase.from("inventory_items").select(`
        *,
        assets (*)
      `);

    // Only add the organization filter if a valid ID is provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getInventoryItem(id: string, organizationId?: string) {
    let query = supabase
      .from("inventory_items")
      .select(
        `
        *,
        assets (*),
        documents (*)
      `
      )
      .eq("id", id);

    // Only add the organization filter if a valid ID is provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query.single();
    if (error) throw error;
    return data;
  },

  async addInventoryItem(
    item: Database["public"]["Tables"]["inventory_items"]["Insert"]
  ) {
    const { data, error } = await supabase
      .from("inventory_items")
      .insert(item)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateInventoryItem(
    id: string,
    updates: Database["public"]["Tables"]["inventory_items"]["Update"],
    organizationId?: string
  ) {
    // Add updated_at timestamp if not provided
    const updatesWithTimestamp = {
      ...updates,
      updated_at: new Date().toISOString(),
    };

    // Build the query
    let query = supabase
      .from("inventory_items")
      .update(updatesWithTimestamp)
      .eq("id", id);

    // Only add organization filter if provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query.select().single();

    if (error) {
      console.error("Error updating inventory item:", error);
      throw error;
    }

    return data;
  },

  // Assets
  async getAsset(barcode: string, organizationId?: string) {
    try {
      console.log(
        `getAsset called with barcode: ${barcode}, organizationId: ${
          organizationId || "none"
        }`
      );

      // Build the query
      let query = supabase
        .from("assets")
        .select(
          `
          *,
          inventory_items!parent_item_id (*),
          maintenance_records (*),
          checkout_records (*),
          documents (*)
        `
        )
        .eq("barcode", barcode);

      // Only add organization filter if provided
      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      // Use maybeSingle instead of single to avoid throwing on no rows
      const { data, error, status } = await query.maybeSingle();

      if (error) {
        console.error(`Error fetching asset (Status: ${status}):`, error);
        throw error;
      }

      if (!data) {
        console.log(`No asset found with barcode: ${barcode}`);
        return null;
      }

      // Log the structure of the returned data to help with debugging
      console.log(`Asset found with barcode: ${barcode}`, {
        hasInventoryItems: !!data.inventory_items,
        inventoryItemsType: typeof data.inventory_items,
        parentItemId: data.parent_item_id,
      });

      return data;
    } catch (error) {
      console.error("Error in getAsset:", error);
      throw error;
    }
  },

  async getAssetById(id: string, organizationId?: string) {
    try {
      console.log(
        `getAssetById called with id: ${id}, organizationId: ${
          organizationId || "none"
        }`
      );

      if (!id) {
        console.error("getAssetById: Missing asset ID");
        return null;
      }

      // Build the query with headers
      let query = supabase
        .from("assets")
        .select(
          `
          *,
          inventory_items!parent_item_id (*),
          maintenance_records (*),
          checkout_records (*),
          documents (*)
        `
        )
        .eq("id", id);

      // Only add organization filter if provided
      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      // Log the query being executed
      console.log(`Executing query for asset ID: ${id}`);

      // Use maybeSingle instead of single to avoid throwing on no rows
      const { data, error, status, statusText } = await query.maybeSingle();

      console.log(`Query response status: ${status} ${statusText || ""}`);

      if (error) {
        console.error(`Error fetching asset by ID (Status: ${status}):`, error);
        throw error;
      }

      if (!data) {
        console.log(`No asset found with ID: ${id}`);
        return null;
      }

      // Log the structure of the returned data to help with debugging
      console.log(`Asset found with ID: ${id}`, {
        hasInventoryItems: !!data.inventory_items,
        inventoryItemsType: typeof data.inventory_items,
        parentItemId: data.parent_item_id,
      });

      return data;
    } catch (error) {
      console.error("Error in getAssetById:", error);
      throw error;
    }
  },

  async getAssetsByInventoryItem(
    inventoryItemId: string,
    organizationId: string
  ) {
    const { data, error } = await supabase
      .from("assets")
      .select("*")
      .eq("parent_item_id", inventoryItemId)
      .eq("organization_id", organizationId);
    if (error) throw error;
    return data;
  },

  async getAllAssets(organizationId?: string, limit = 100) {
    try {
      // Build the query
      let query = supabase
        .from("assets")
        .select(
          `
        id,
        barcode,
        serial_number,
        status,
        organization_id,
        parent_item_id,
        inventory_items!parent_item_id (name)
      `
        )
        .order("created_at", { ascending: false })
        .limit(limit);

      // Only add organization filter if provided
      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching all assets:", error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error("Error in getAllAssets:", error);
      throw error;
    }
  },

  async addAsset(asset: Database["public"]["Tables"]["assets"]["Insert"]) {
    try {
      console.log("Adding asset:", asset);

      // Check if organization_id is missing
      if (!asset.organization_id) {
        console.warn(
          "Asset is missing organization_id! This will cause issues with filtering."
        );

        // Try to get the organization ID from the parent item
        if (asset.parent_item_id) {
          console.log(
            "Attempting to get organization_id from parent inventory item"
          );
          const { data: parentItem } = await supabase
            .from("inventory_items")
            .select("organization_id")
            .eq("id", asset.parent_item_id)
            .single();

          if (parentItem?.organization_id) {
            console.log(
              `Using organization_id ${parentItem.organization_id} from parent item`
            );
            asset.organization_id = parentItem.organization_id;
          }
        }
      }

      // Final check to ensure we have an organization_id
      if (!asset.organization_id) {
        console.error("Failed to determine organization_id for asset");
      }

      const { data, error } = await supabase
        .from("assets")
        .insert(asset)
        .select()
        .single();

      if (error) {
        console.error("Error adding asset:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in addAsset:", error);
      throw error;
    }
  },

  async updateAsset(
    id: string,
    updates: Database["public"]["Tables"]["assets"]["Update"],
    organizationId: string
  ) {
    // Add updated_at timestamp if not provided
    const updatesWithTimestamp = {
      ...updates,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("assets")
      .update(updatesWithTimestamp)
      .eq("id", id)
      .eq("organization_id", organizationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Maintenance Records
  async getMaintenanceRecords(assetId: string, organizationId: string) {
    const { data, error } = await supabase
      .from("maintenance_records")
      .select("*")
      .eq("asset_id", assetId)
      .eq("organization_id", organizationId)
      .order("date", { ascending: false });
    if (error) throw error;
    return data;
  },

  async addMaintenanceRecord(
    record: Database["public"]["Tables"]["maintenance_records"]["Insert"]
  ) {
    const { data, error } = await supabase
      .from("maintenance_records")
      .insert(record)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateMaintenanceRecord(
    id: string,
    updates: Database["public"]["Tables"]["maintenance_records"]["Update"],
    organizationId: string
  ) {
    const { data, error } = await supabase
      .from("maintenance_records")
      .update(updates)
      .eq("id", id)
      .eq("organization_id", organizationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteMaintenanceRecord(id: string, organizationId: string) {
    const { error } = await supabase
      .from("maintenance_records")
      .delete()
      .eq("id", id)
      .eq("organization_id", organizationId);
    if (error) throw error;
    return true;
  },

  // Checkout Records
  async getCheckoutRecords(assetId: string, organizationId: string) {
    const { data, error } = await supabase
      .from("checkout_records")
      .select("*")
      .eq("asset_id", assetId)
      .eq("organization_id", organizationId)
      .order("checkout_date", { ascending: false });
    if (error) throw error;
    return data;
  },

  async addCheckoutRecord(
    record: Database["public"]["Tables"]["checkout_records"]["Insert"]
  ) {
    const { data, error } = await supabase
      .from("checkout_records")
      .insert(record)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async returnAsset(checkoutRecordId: string, organizationId: string) {
    const { data, error } = await supabase
      .from("checkout_records")
      .update({ return_date: new Date().toISOString() })
      .eq("id", checkoutRecordId)
      .eq("organization_id", organizationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Documents
  async getDocuments(params: {
    inventoryItemId?: string;
    assetId?: string;
    jobId?: string;
    organizationId: string;
  }) {
    let query = supabase
      .from("documents")
      .select("*")
      .eq("organization_id", params.organizationId);

    if (params.inventoryItemId) {
      query = query.eq("inventory_item_id", params.inventoryItemId);
    }
    if (params.assetId) {
      query = query.eq("asset_id", params.assetId);
    }
    if (params.jobId) {
      query = query.eq("job_id", params.jobId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async addDocument(
    document: Database["public"]["Tables"]["documents"]["Insert"]
  ) {
    const { data, error } = await supabase
      .from("documents")
      .insert(document)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateDocument(
    id: string,
    updates: Database["public"]["Tables"]["documents"]["Update"],
    organizationId: string
  ) {
    // Add updated_at timestamp if not provided
    const updatesWithTimestamp = {
      ...updates,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("documents")
      .update(updatesWithTimestamp)
      .eq("id", id)
      .eq("organization_id", organizationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteDocument(id: string, organizationId: string) {
    const { error } = await supabase
      .from("documents")
      .delete()
      .eq("id", id)
      .eq("organization_id", organizationId);
    if (error) throw error;
    return true;
  },
};

// Storage service for file uploads - TODO: Add upload logic later
export const storageService = {
  // Upload a file to Supabase storage
  async uploadFile(_bucket: string, _filePath: string, _file: File) {
    // TODO: Add upload logic later
    console.warn("File upload not implemented yet");
    return null;
  },

  // Get public URL for a file
  getPublicUrl(_bucket: string, _filePath: string) {
    // TODO: Add public URL logic later
    return "";
  },

  // Upload a document file and return its public URL
  async uploadDocument(_file: File, _userId: string, _organizationId: string) {
    // TODO: Add document upload logic later
    console.warn("Document upload not implemented yet");
    return "";
  },

  // Upload an image file and return its public URL
  async uploadImage(_file: File, _userId: string, _organizationId: string) {
    // TODO: Add image upload logic later
    console.warn("Image upload not implemented yet");
    return "";
  },

  // Upload multiple image files and return their public URLs
  async uploadImages(_files: File[], _userId: string, _organizationId: string) {
    // TODO: Add multiple image upload logic later
    console.warn("Multiple image upload not implemented yet");
    return [];
  },
};

// Job service for job-related operations
export const jobService = {
  // Get all jobs for an organization
  async getJobs(organizationId?: string) {
    // If no organizationId is provided, don't filter by it
    let query = supabase.from("jobs").select("*");

    // Only add the organization filter if a valid ID is provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get a specific job by ID
  async getJob(id: string, organizationId?: string) {
    let query = supabase.from("jobs").select("*").eq("id", id);

    // Only add the organization filter if a valid ID is provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query.single();
    if (error) throw error;
    return data;
  },

  // Add a new job
  async addJob(job: Database["public"]["Tables"]["jobs"]["Insert"]) {
    console.log("Adding job with data:", JSON.stringify(job, null, 2));

    const { data, error } = await supabase
      .from("jobs")
      .insert(job)
      .select()
      .single();

    if (error) {
      console.error("Error adding job:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      throw error;
    }

    console.log("Job added successfully:", data);
    return data;
  },

  // Update an existing job
  async updateJob(
    id: string,
    updates: Database["public"]["Tables"]["jobs"]["Update"],
    organizationId: string
  ) {
    // Add updated_at timestamp if not provided
    const updatesWithTimestamp = {
      ...updates,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("jobs")
      .update(updatesWithTimestamp)
      .eq("id", id)
      .eq("organization_id", organizationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Delete a job
  async deleteJob(id: string, organizationId: string) {
    const { error } = await supabase
      .from("jobs")
      .delete()
      .eq("id", id)
      .eq("organization_id", organizationId);
    if (error) throw error;
    return true;
  },
};

// Crew service for crew-related operations
export const crewService = {
  // Get all crew members for an organization
  async getCrewMembers(organizationId?: string) {
    // If no organizationId is provided, don't filter by it
    let query = supabase.from("crew_members").select("*");

    // Only add the organization filter if a valid ID is provided
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get a specific crew member by ID
  async getCrewMember(id: string, organizationId?: string) {
    try {
      console.log("getCrewMember called with:", { id, organizationId });

      if (!id) {
        throw new Error("Crew member ID is required");
      }

      // First, ensure the user has access to this organization
      if (organizationId) {
        try {
          // Get current user
          const {
            data: { user },
          } = await supabase.auth.getUser();

          if (!user) {
            console.warn("No authenticated user found");
            throw new Error("User not authenticated");
          }

          console.log("Current user:", user.id);

          // Check if the user is a member of the organization
          const { data: membership, error: membershipError } = await supabase
            .from("organization_members")
            .select("*")
            .eq("user_id", user.id)
            .eq("organization_id", organizationId)
            .maybeSingle();

          console.log("Organization membership check:", {
            membership,
            membershipError,
          });

          if (membershipError) {
            console.warn(
              "Error checking organization membership:",
              membershipError
            );
          } else if (!membership) {
            console.log("User is not a member of this organization");

            // Check if organization_members table exists
            const { count, error: tableError } = await supabase
              .from("organization_members")
              .select("*", { count: "exact", head: true });

            if (tableError) {
              console.warn(
                "Error checking organization_members table:",
                tableError
              );

              // Try to create the membership if the table exists
              const { error: insertError } = await supabase
                .from("organization_members")
                .insert({
                  user_id: user.id,
                  organization_id: organizationId,
                  role: "member",
                });

              if (insertError) {
                console.warn(
                  "Error creating organization membership:",
                  insertError
                );
              } else {
                console.log("Created organization membership for user");
              }
            } else {
              console.log(
                "organization_members table exists with",
                count,
                "records"
              );
            }
          }
        } catch (err) {
          console.warn("Error checking organization membership:", err);
          // Continue anyway, as RLS will handle permissions
        }
      }

      // Build the query
      let query = supabase.from("crew_members").select("*").eq("id", id);

      // Only add the organization filter if a valid ID is provided
      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      console.log("Executing query:", query);
      const { data, error, status, statusText } = await query.single();

      console.log("Query response:", { data, error, status, statusText });

      if (error) {
        console.error("Database error fetching crew member:", error);
        if (error.code === "PGRST116") {
          throw new Error(
            `Crew member not found. The crew_members table may not exist yet. Details: ${JSON.stringify(
              error
            )}`
          );
        }

        // Check for auth errors
        if (error.code === "42501") {
          throw new Error("You don't have permission to view this crew member");
        }

        throw new Error(
          `Database error: ${error.message}. Details: ${JSON.stringify(error)}`
        );
      }

      if (!data) {
        throw new Error("Crew member not found - no data returned");
      }

      // Ensure all required fields are present with proper defaults
      const processedData = {
        ...data,
        // Ensure arrays have proper defaults
        specialties: Array.isArray(data.specialties) ? data.specialties : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        // Ensure objects have proper defaults
        payment_details: data.payment_details || {},
        address: data.address || {},
        availability: data.availability || {},
        emergency_contact: data.emergency_contact || {},
        // Handle documents specially since it needs to be an array of objects
        documents: Array.isArray(data.documents) ? data.documents : [],
        // Handle certifications if present
        certifications: Array.isArray(data.certifications)
          ? data.certifications
          : [],
      };

      // Ensure documents have the required structure
      if (processedData.documents.length > 0) {
        // Check if the first item has the expected structure
        const firstDoc = processedData.documents[0];
        if (typeof firstDoc === "string" || !firstDoc.id) {
          // Convert simple array values to proper document objects
          processedData.documents = processedData.documents.map(
            (doc: any, index: number) => {
              if (typeof doc === "string") {
                return {
                  id: `doc-${index}`,
                  name: `Document ${index + 1}`,
                  url: doc,
                  type: "other",
                  uploaded_at: data.created_at || new Date().toISOString(),
                };
              } else if (typeof doc === "object") {
                return {
                  id: doc.id || `doc-${index}`,
                  name: doc.name || `Document ${index + 1}`,
                  url: doc.url || "",
                  type: doc.type || "other",
                  uploaded_at:
                    doc.uploaded_at ||
                    data.created_at ||
                    new Date().toISOString(),
                };
              }
              return {
                id: `doc-${index}`,
                name: `Document ${index + 1}`,
                url: "",
                type: "other",
                uploaded_at: data.created_at || new Date().toISOString(),
              };
            }
          );
        }
      }

      return processedData;
    } catch (err) {
      console.error("Error in getCrewMember:", err);
      throw err;
    }
  },

  // Add a new crew member
  async addCrewMember(
    crewMember: Database["public"]["Tables"]["crew_members"]["Insert"]
  ) {
    try {
      console.log(
        "Adding crew member with data:",
        JSON.stringify(crewMember, null, 2)
      );

      // Ensure required fields are present
      if (
        !crewMember.first_name ||
        !crewMember.last_name ||
        !crewMember.role_type
      ) {
        throw new Error(
          "Missing required fields: first_name, last_name, or role_type"
        );
      }

      // Ensure arrays are properly initialized
      crewMember.specialties = Array.isArray(crewMember.specialties)
        ? crewMember.specialties
        : [];
      crewMember.tags = Array.isArray(crewMember.tags) ? crewMember.tags : [];

      // Ensure organization_id, created_by, and updated_by are set
      if (!crewMember.organization_id) {
        throw new Error("Missing organization_id");
      }

      if (!crewMember.created_by) {
        throw new Error("Missing created_by");
      }

      if (!crewMember.updated_by) {
        throw new Error("Missing updated_by");
      }

      // Get the JWT to check if it contains org_id
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const jwtToken = session?.access_token;

      if (jwtToken) {
        try {
          // Parse the JWT token to check for org_id
          const base64Url = jwtToken.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join("")
          );

          const payload = JSON.parse(jsonPayload);
          console.log("JWT payload:", payload);

          // If the JWT doesn't have org_id, we need to add the user to the organization_members table
          if (!payload.org_id) {
            console.log(
              "No org_id in JWT, ensuring user is in organization_members table"
            );

            // Check if the user is already a member of this organization
            const { data: existingMembership } = await supabase
              .from("organization_members")
              .select("*")
              .eq("user_id", crewMember.created_by)
              .eq("organization_id", crewMember.organization_id)
              .single();

            if (!existingMembership) {
              console.log("Adding user to organization_members table");
              // Add the user to the organization
              await supabase.from("organization_members").insert({
                user_id: crewMember.created_by,
                organization_id: crewMember.organization_id,
                role: "member",
              });
            }
          }
        } catch (e) {
          console.error("Error parsing JWT:", e);
        }
      }

      const { data, error, status } = await supabase
        .from("crew_members")
        .insert(crewMember)
        .select()
        .single();

      console.log("Insert response:", { data, error, status });

      if (error) {
        console.error("Database error:", error);
        throw new Error(`Database error (${error.code}): ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error in addCrewMember:", error);
      throw error;
    }
  },

  // Update an existing crew member
  async updateCrewMember(
    id: string,
    updates: Database["public"]["Tables"]["crew_members"]["Update"],
    organizationId: string
  ) {
    try {
      console.log("Updating crew member:", { id, organizationId });
      console.log("Update data:", JSON.stringify(updates, null, 2));

      if (!id) {
        throw new Error("Crew member ID is required");
      }

      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      // Add updated_at timestamp if not provided
      const updatesWithTimestamp = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // Get current user for updated_by if not provided
      if (!updatesWithTimestamp.updated_by) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          updatesWithTimestamp.updated_by = user.id;
        }
      }

      const { data, error, status } = await supabase
        .from("crew_members")
        .update(updatesWithTimestamp)
        .eq("id", id)
        .eq("organization_id", organizationId)
        .select()
        .single();

      console.log("Update response:", { data, error, status });

      if (error) {
        console.error("Error updating crew member:", error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        throw new Error("No data returned after update");
      }

      return data;
    } catch (err) {
      console.error("Error in updateCrewMember:", err);
      throw err;
    }
  },

  // Delete a crew member
  async deleteCrewMember(id: string, organizationId: string) {
    const { error } = await supabase
      .from("crew_members")
      .delete()
      .eq("id", id)
      .eq("organization_id", organizationId);
    if (error) throw error;
    return true;
  },

  // Upload a crew member profile image
  async uploadProfileImage(
    _file: File,
    _userId: string,
    _organizationId: string
  ) {
    return storageService.uploadImage(_file, _userId, _organizationId);
  },

  // Upload a document for a crew member
  async uploadDocument(_file: File, _userId: string, _organizationId: string) {
    return storageService.uploadDocument(_file, _userId, _organizationId);
  },
};
